/** @jsxImportSource ./simboluri-jsx */
// NOTA DE CALCUL COMPLETĂ a unui element, în josul paginii lui: tot ce
// arăta pagina pasului care l-a armat, pe elementul ăsta — secțiunile,
// formulele, tabelele de armare, croiala barelor, forfecarea, fisurarea,
// ancorajele. Aceleași componente ca paginile pașilor, cu vederile pe care
// pagina elementului le are deja deasupra (3D, localizarea, înfășurătoarea)
// lăsate deoparte (`faraVederi`), ca nimic să nu mai ceară drumul prin
// lanțul de calcul.

import { useNavigate } from "react-router-dom";
import { useT } from "../i18n";
import type { ArmareResult, ArmarePeretiResult, ArmareStalpiResult, FemResult,
              StepState } from "./tipuri";
import type { ElementIndexat } from "./elemente";
import { hashElement, hashSectiune, nivelDinRef } from "./legaturi";
import { NotaCalcul, croireAxului } from "./StepArmareGrinzi";
import { StalpDetaliu } from "./StepArmareStalpi";
import { NotaCalculPerete } from "./StepArmarePereti";
import LameleArmate from "./LameleArmate";
import { CardOchi, PlanOchi, type Ochi, type Rezultat as RezultatPlaca }
  from "./StepArmarePlaca";
import { CardAx } from "./StepArmareFundatie";
import type { AxArmat, RezultatArmareFundatie } from "./armareAx";
import type { Cheie } from "../i18n";

/** Pasul al cărui rezultat poartă nota elementului, pe familie. */
const PAS_NOTEI: Partial<Record<ElementIndexat["tip"], string>> = {
  grinda: "armare_grinzi",
  stalp: "armare_stalpi",
  perete: "armare_pereti",
  lamela: "armare_pereti",
  ochi: "armare_placa",
  ax: "armare_fundatie",
};

const NUME_PAS: Record<string, Cheie> = {
  armare_grinzi: "struct.elp.nota_pas.armare_grinzi",
  armare_stalpi: "struct.elp.nota_pas.armare_stalpi",
  armare_pereti: "struct.elp.nota_pas.armare_pereti",
  armare_placa: "struct.elp.nota_pas.armare_placa",
  armare_fundatie: "struct.elp.nota_pas.armare_fundatie",
};

export default function NotaElement({ el, steps, id, solutie }: {
  el: ElementIndexat;
  steps: Record<string, StepState>;
  id: string;
  solutie?: string | null;
}) {
  const t = useT();
  const navigate = useNavigate();
  const pas = PAS_NOTEI[el.tip];
  const rezultat = pas ? steps[pas]?.result : null;
  if (!pas || !rezultat) return null;
  const model = (steps["model_fem"]?.result as FemResult | null | undefined)?.model ?? null;
  // un clic pe ALT element într-un desen al notei duce la pagina lui
  const laElement = (ref: string) => navigate(hashElement(id, ref, solutie ?? null));

  let continut: React.ReactNode = null;
  /* eslint-disable @typescript-eslint/no-explicit-any */
  if (el.tip === "grinda" && el.brut) {
    const r = rezultat as unknown as ArmareResult;
    continut = (
      <NotaCalcul g={el.brut} p={r.parametri} croire={croireAxului(r, el.brut)}
                  model={model} projectId={id} solutie={solutie} peGrinda={laElement}
                  faraVederi />
    );
  } else if (el.tip === "stalp" && el.brutParinte) {
    const r = rezultat as unknown as ArmareStalpiResult;
    continut = (
      <StalpDetaliu s={el.brutParinte} model={model}
                    cCm={Number((r.parametri as any)?.c_stalp_cm ?? 4) || 4}
                    focus={{ ref: el.ref, nivel: nivelDinRef(el.ref), n: 0 }}
                    projectId={id} solutie={solutie} peStalp={laElement}
                    faraVederi />
    );
  } else if (el.tip === "perete" && el.brut) {
    continut = (
      <NotaCalculPerete p={el.brut} r={rezultat as unknown as ArmarePeretiResult} model={model}
                        projectId={id} solutie={solutie} pePerete={laElement}
                        faraVederi />
    );
  } else if (el.tip === "lamela") {
    continut = <LameleArmate r={rezultat as unknown as ArmarePeretiResult} element={el.ref} />;
  } else if (el.tip === "ochi" && el.brut) {
    const ochi = el.brut as Ochi;
    const panou = (model?.panouri ?? []).find((p) => p.id === ochi.id) ?? null;
    continut = (
      <>
        <div className="st-findings">
          <PlanOchi ochi={ochi} panou={panou} peVecin={laElement} />
        </div>
        <CardOchi ochi={ochi} r={rezultat as unknown as RezultatPlaca} panouGeo={panou} />
      </>
    );
  } else if (el.tip === "ax" && (el.brut as any)?.armare) {
    continut = (
      <CardAx ax={(el.brut as any).armare as AxArmat}
              r={rezultat as unknown as RezultatArmareFundatie}
              projectId={id} solutie={solutie} faraVederi />
    );
  }
  /* eslint-enable @typescript-eslint/no-explicit-any */
  if (!continut) return null;
  return (
    <section className="ob-card el-nota" data-testid="nota-element">
      <div className="el-nota-cap">
        <span className="ob-lbl">{t("struct.elp.nota_completa")}</span>
        <span className="sg-muted">
          {t("struct.elp.nota_sub", { pas: t(NUME_PAS[pas] ?? "struct.elp.nota_pas.armare_grinzi") })}{" "}
          <a href={hashSectiune(id, pas, solutie ?? null,
                                { ref: el.ref, nivel: nivelDinRef(el.ref) })}>
            {t("struct.elp.nota_pagina_pasului")}
          </a>
        </span>
      </div>
      <div className="st-wrap el-nota-corp">{continut}</div>
    </section>
  );
}
